import * as React from "react";
import i18next from "i18next";;
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { useReadLocalStorage } from "usehooks-ts";
import { fetcher } from "~/hooks/useFetcher";
import NoResult from "../Search/Results/NoResult";
import DocumentItem, { SourceDocument } from "./DocumentItem";

const CategoryItems: React.FunctionComponent = () => {
    const token = useReadLocalStorage("_t") as string;
    const { language } = i18next;

    const { categoryId } = useParams();

    const { data, isLoading, mutate } = useSWR(
        `/api/market-intelligence/search?marketIntelligenceCategoryId=${categoryId}`,
        (url) => fetcher(url, token)
    );

    React.useEffect(() => {
        mutate();
    }, [language]);

    const renderSkeleton = () => (
        <>
            {[...Array(10)].map((_, index) => (
                <div
                    key={`reader-intelligence-skeleton-${index}`}
                    className="w-full max-w-[660px] h-60 bg-white rounded-md shadow-sm overflow-hidden animate-pulse flex"
                >
                    {/* Thumbnail Skeleton */}
                    <div className="w-[208px] h-full p-4 flex items-center justify-center bg-gray-100">
                        <div className="w-[146px] h-[208px] bg-gray-300 rounded-md" />
                    </div>

                    {/* Content Skeleton */}
                    <div className="flex flex-col justify-between flex-1 px-4 py-6 gap-4">
                        {/* Title & Tags */}
                        <div>
                            <div className="h-5 bg-gray-300 rounded w-3/4 mb-2" />
                            <div className="h-4 bg-gray-200 rounded w-1/2" />
                        </div>

                        {/* Description */}
                        <div className="space-y-2">
                            <div className="h-3 bg-gray-200 rounded w-full" />
                            <div className="h-3 bg-gray-200 rounded w-5/6" />
                            <div className="h-3 bg-gray-200 rounded w-2/3" />
                            <div className="h-3 bg-gray-200 rounded w-4/6" />
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-2">
                            <div className="w-[121px] h-[36px] bg-gray-300 rounded-md" />
                            <div className="w-[121px] h-[36px] bg-gray-300 rounded-md" />
                        </div>
                    </div>
                </div>
            ))}
        </>
    );

    const renderContent = () => {
        if (isLoading) {
            return renderSkeleton();
        }

        if (data?.MarketIntelligence?.length === 0) {
            return <NoResult />;
        }

        return data?.MarketIntelligence?.map((item: SourceDocument, index: number) => (
            <DocumentItem key={index} item={item} />
        ));
    };

    return (
        <div className="w-screen bg-white relative z-8 overflow-hidden flex flex-col">
            <h1 className="bg-white border-b border-[#A6A09B33] text-sm text-black text-left font-medium-light py-4 px-10 gap-[26px] leading-5 tracking-[-2%]">
                {data?.DocumentTypeName || ""}
            </h1>
            <div className="grid grid-cols-1 md:grid-cols-2 flex-grow overflow-auto place-items-center gap-10 p-10">
                {renderContent()}
            </div>
        </div>
    );
};

export default CategoryItems;
